<template>
  <div class="p-6">
    <div class="mb-6">
      <h2 class="text-2xl font-minion font-semibold text-[#00423D] mb-4">
        Year 1 Annual Operating Budget Summary
      </h2>
    </div>

    <div v-if="isLoading" class="text-gray-600 font-sans py-4">
      <p>Loading data...</p>
    </div>
    <div v-else-if="errorMessage" class="p-4 bg-red-50 font-sans border border-red-200 rounded-md mb-4">
      <p class="text-red-700">{{ errorMessage }}</p>
    </div>
    <div v-else class="bg-white rounded-xl shadow-sm">
      <div class="space-y-12">
        <!-- Expenses Section -->
        <div>
          <div class="font-sans text-[16px] font-semibold text-[#414141] mb-2 border-b border-[#00423D] pb-1">YEAR 1 EXPENSES</div>
          <div class="flex flex-col md:flex-row w-full items-center gap-8">
            <!-- Pie Chart -->
            <div class="w-full md:w-1/3 flex justify-center items-center mb-6 mt-2 md:mb-0">
              <div class="w-[220px] h-[220px] flex-shrink-0">
                <PieChart
                  :labels="[ 'Core Annual Programs Combined', ...overheadGroupLabels ]"
                  :data="expenseData"
                  :backgroundColors="[ '#00423D', ...overheadGroupColors ]"
                  :options="{
                    plugins: { 
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.label}: ${context.raw} %`
                        }
                      }
                    }
                  }"
                />
              </div>
            </div>
            <!-- Custom Legend -->
            <div class="w-full md:w-1/3 flex flex-col justify-center items-start px-2 mb-6 md:mb-0">
              <div class="flex items-center mb-2">
                <span class="w-4 h-4 rounded-[3px] border-[0.5px] border-[#00423D] mr-2 inline-block" style="background: #00423D"></span>
                <span class="font-sans text-[12px] font-semibold text-[#414141] align-middle" style="line-height: 1.05; letter-spacing: 0;">Core Annual Programs Combined</span>
              </div>
              <div class="font-sans text-[14px] font-semibold text-[#414141] mb-2 mt-2 border-b-[0.5px] border-[#00423D] pb-1">YEAR 1 OVERHEAD EXPENSES</div>
              <div v-for="(label, idx) in overheadGroupLabels" :key="label" class="flex items-center mb-1">
                <span class="w-4 h-4 rounded-[3px] border-[0.5px] border-[#00423D] mr-2 inline-block" :style="{ background: overheadGroupColors[idx] }"></span>
                <span class="font-sans text-[12px] font-semibold text-[#414141] align-middle" style="line-height: 1.05; letter-spacing: 0;">{{ label }}</span>
              </div>
            </div>
            <!-- Calculation Boxes -->
            <div class="w-full md:w-1/3 flex flex-col gap-2 justify-center items-center mt-2 px-2">
              <div class="w-[184px] h-[60px] rounded-[10px] border border-[#00423D] bg-[#EDEFEE] flex flex-col justify-center items-center shadow-sm p-5">
                <span class="font-sans text-[10px] font-semibold text-[#00423D] uppercase leading-[105%] text-center mt-2 mb-1">CORE ANNUAL PROGRAM COMBINED EXPENSES</span>
                <span class="font-minion text-[18px] font-semibold text-[#00423D] text-center">{{ formatUSD(coreAnnualProgramExpenses) }}</span>
              </div>
              <span class="text-2xl text-[#00423D] font-bold my-[-8px]">+</span>
              <div class="w-[184px] h-[60px] rounded-[10px] border border-[#00423D] bg-[#EDEFEE] flex flex-col justify-center items-center shadow-sm p-5">
                <span class="font-sans text-[10px] font-semibold text-[#00423D] uppercase leading-[105%] text-center mt-2 mb-1">YEAR 1 OVERHEAD EXPENSES</span>
                <span class="font-minion text-[18px] font-semibold text-[#00423D] text-center">{{ formatUSD(overheadExpenses) }}</span>
              </div>
              <span class="text-2xl text-[#00423D] font-bold my-[-8px]">=</span>
              <div class="w-[184px] h-[60px] rounded-[10px] border border-[#00423D] bg-[#EDEFEE] flex flex-col justify-center items-center shadow-sm p-5">
                <span class="font-sans text-[10px] font-semibold text-[#00423D] uppercase leading-[105%] text-center mt-2 mb-1">YEAR 1 TOTAL ANNUAL EXPENSES</span>
                <span class="font-minion text-[18px] font-semibold text-[#00423D] text-center">{{ formatUSD(totalExpenses) }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Revenue Section -->
        <div>
          <div class="font-sans text-[16px] font-semibold text-[#414141] mb-2 border-b border-[#00423D] pb-1">YEAR 1 REVENUE</div>
          <div class="flex flex-col md:flex-row w-full items-center gap-8">
            <!-- Pie Chart -->
            <div class="w-full md:w-1/3 flex justify-center items-center mb-6 mt-2 md:mb-0">
              <div class="w-[220px] h-[220px] flex-shrink-0">
                <PieChart
                  :labels="revenueGroupLabels"
                  :data="revenueData"
                  :backgroundColors="revenueGroupColors"
                  :options="{
                    plugins: { 
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.label}: ${context.raw} %`
                        }
                      }
                    }
                  }"
                />
              </div>
            </div>
            <!-- Custom Legend -->
            <div class="w-full md:w-1/3 flex flex-col justify-center items-start px-2 mb-6 md:mb-0">
              <div v-for="(label, idx) in revenueGroupLabels" :key="label" class="flex items-center mb-2">
                <span class="w-4 h-4 rounded-[3px] border-[0.5px] border-[#00423D] mr-2 inline-block" :style="{ background: revenueGroupColors[idx] }"></span>
                <span class="font-sans text-[12px] font-semibold text-[#414141] align-middle" style="line-height: 1.05; letter-spacing: 0;">{{ label }}</span>
              </div>
            </div>
            <!-- Calculation Box -->
            <div class="w-full md:w-1/3 flex flex-col justify-center items-center px-2">
              <div class="w-[184px] h-[60px] rounded-[10px] border border-[#00423D] bg-[#EDEFEE] flex flex-col justify-center items-center shadow-sm p-5">
                <span class="font-sans text-[10px] font-semibold text-[#00423D] uppercase leading-[105%] text-center mt-2 mb-1">YEAR 1 TOTAL REVENUE</span>
                <span class="font-minion text-[18px] font-semibold text-[#00423D] text-center">{{ formatUSD(totalRevenue) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useStepValidation } from '@/composables/useStepValidation';
import PieChart from '@/components/PieChart.vue';
import global from '@/global';
import { formatUSD } from '@/composables/numberUtils';
const isLoading = ref(true);
const errorMessage = ref("");
const coreAnnualProgramExpenses = ref(0);
const overheadExpenses = ref(0);
const totalExpenses = ref(0);
const totalRevenue = ref(0);

// Dynamic Overhead Expenses Pie Chart Data
const overheadGroupLabels = ref([]);
const overheadGroupData = ref([]);
const overheadGroupColors = ref([]);

// Dynamic Revenue Pie Chart Data
const revenueGroupLabels = ref([]);
const revenueGroupData = ref([]);
const revenueGroupColors = ref([]);

// Desired order for overhead expense types
const overheadOrder = [
  "Personnel and Benefits",
  "Professional and Contractual Fees",
  "Printing and Publications",
  "Travel, Conferences, and Meetings",
  "Marketing and Communications",
  "Facilities",
  "Other Expenses",
  "In-Kind Goods & Services"
];

// Desired order for revenue types
const revenueOrder = [
  "Philanthropic Contributions",
  "Earned Revenue",
  "Investment Income",
  "Government Appropriations",
  "In-Kind Goods/Services"
];

// Color palette generator (expand as needed)
function generateThemeColors(count) {
  const palette = [
  '#B4E2DE', '#8DA195', '#EBE8E0', '#80A09E', '#00423D', '#00796B', '#6DB3A0', '#BFD8B8', '#2E8B57', '#3D9A6D', '#005850', '#A7C4BC', '#B0D1C6', '#7DC6AF',
  ];
  return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
}

const expenseLabels = [
  'Core Annual Programs Combined',
  // Overhead group labels will be appended dynamically
];
const expenseColors = [
  '#00423D',
  // Overhead group colors will be appended dynamically
];

const expenseData = computed(() => [
  coreAnnualProgramExpenses.value,
  ...overheadGroupData.value
]);

const revenueLabels = [
  'Philanthropic Contributions',
  'Earned Revenue',
  'Investment Income',
  'Government Appropriations',
  'In Kind'
];
const revenueColors = [
  '#8DA195', '#EBE8E0', '#80A09E', '#B4E2DE', '#00423D',
];

const revenueData = computed(() => revenueGroupData.value);

const authStore = useAuthStore();

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";
    await authStore.fetchUserData();
    if (!authStore.user?.accessToken) {
      throw new Error('Unable to load user authentication. Please try refreshing the page.');
    }
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/annual_operating_budget_summary_year_1`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    coreAnnualProgramExpenses.value = data.core_programs_expenses_year_1 || 0;
    overheadExpenses.value = data.overhead_expenses_year_1 || 0;
    totalExpenses.value = data.total_annual_expenses_year_1 || 0;
    totalRevenue.value = data.total_revenue_year_1 || 0;

    // Set dynamic overhead group data in the specified order
    const groupSums = data.overhead_expenses_year_1_grouped?.group_sums || {};
    const orderedLabels = [];
    const orderedData = [];
    overheadOrder.forEach(type => {
      if (groupSums[type] !== undefined) {
        orderedLabels.push(type);
        orderedData.push(groupSums[type]);
      }
    });
    overheadGroupLabels.value = orderedLabels;
    overheadGroupData.value = orderedData;
    overheadGroupColors.value = generateThemeColors(orderedLabels.length);

    // Set dynamic revenue group data in the specified order
    const revenueSums = data.revenue_year_1_grouped?.group_sums || {};
    const revOrderedLabels = [];
    const revOrderedData = [];
    revenueOrder.forEach(type => {
      if (revenueSums[type] !== undefined) {
        revOrderedLabels.push(type);
        revOrderedData.push(revenueSums[type]);
      }
    });
    revenueGroupLabels.value = revOrderedLabels;
    revenueGroupData.value = revOrderedData;
    revenueGroupColors.value = generateThemeColors(revOrderedLabels.length);
  } catch (error) {
    errorMessage.value = error.message || 'Failed to load budget summary data.';
    console.error(error);
  } finally {
    isLoading.value = false;
  }
});

// Use the validation composable with default settings
useStepValidation();
</script>